// src/pages/Login.jsx - Apartment Management System
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiUserLine, RiBuildingLine, RiLoginBoxLine, RiErrorWarningLine, RiShieldKeyholeLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { loginUser, verifyOTP, resendOTP } from '../models/authModel';
import Navbar from './home/Navbar';
import AuthLanguageSelector from './components/AuthLanguageSelector';

const Login = () => {
  const { t, i18n } = useTranslation();

  const { isDarkMode , isRTL } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('login'); // 'login' or 'otp'
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });

    // Clear errors when typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Clear login error when typing
    if (loginError) {
      setLoginError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!credentials.username.trim()) {
      newErrors.username = t('username_required');
    }

    if (!credentials.password) {
      newErrors.password = t('password_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await loginUser(credentials.username, credentials.password);

      if (response.success) {
        if (response.requireOtp) {
          setStep('otp');
          setUserId(response.userId);
          setOtpMessage(response.message || t('verification_code_sent'));
          setLoginError(''); // Clear any previous errors
          // No navigation needed, step change handles UI
        } else if (response.token) {
          // Use AuthContext login method
          login(response.user, response.token);

          // Role-based redirection
          let redirectTo = location.state?.from?.pathname;

          if (!redirectTo || redirectTo === '/admin/dashboard' || redirectTo === '/') {
            if (response.user.role === 'vendor') {
              redirectTo = '/portal/vendor/dashboard';
            } else if (response.user.role === 'tenant') {
              redirectTo = '/portal/tenant/dashboard';
            } else if (response.user.role === 'owner') {
              redirectTo = '/portal/owner/dashboard';
            } else {
              redirectTo = '/admin/dashboard';
            }
          }

          navigate(redirectTo);
        } else {
          setLoginError(response.message || t('login_failed'));
        }
      } else {
        setLoginError(response.message || t('login_failed'));
      }
    } catch (error) {
      console.error('Login error:', error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length < 6) {
      setLoginError(t('enter_valid_otp'));
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await verifyOTP(userId, otp);

      if (response.success) {
        login(response.user, response.token);

        // Role-based redirection
        let redirectTo = location.state?.from?.pathname;

        if (!redirectTo || redirectTo === '/admin/dashboard' || redirectTo === '/') {
          if (response.user.role === 'vendor') {
            redirectTo = '/portal/vendor/dashboard';
          } else if (response.user.role === 'tenant') {
            redirectTo = '/portal/tenant/dashboard';
          } else if (response.user.role === 'owner') {
            redirectTo = '/portal/owner/dashboard';
          } else {
            redirectTo = '/admin/dashboard';
          }
        }

        navigate(redirectTo);
      } else {
        setLoginError(response.message || t('verification_failed'));
      }
    } catch (error) {
      console.error('OTP error:', error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setLoginError('');
    try {
      // userId must be set from initial login response
      const response = await resendOTP(userId);
      setOtpMessage(response.message || t('code_resent_success'));
      // Ideally utilize a success state/toast but using error box for now as feedback
    } catch (error) {
      console.error('Resend error:', error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        setLoginError(t('invalid_credentials'));
      } else if (status === 403) {
        setLoginError(t('account_deactivated'));
      } else if (status === 429) {
        // Global interceptor already shows a toast, but we can set a local message too
        setLoginError(data?.message || t('too_many_attempts'));
      } else {
        setLoginError(data?.message || t('login_failed'));
      }
    } else if (error.request) {
      setLoginError(t('server_no_response'));
    } else {
      setLoginError(error.message || t('login_failed'));
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-zinc-950' : 'bg-white'}`}>
      <Navbar />
      <div className={`max-w-md w-full relative z-10 ${isDarkMode ? 'bg-zinc-900 border-white/[0.06]' : 'bg-white border-black/[0.1]'} border rounded-xl shadow-2xl overflow-hidden transition-all duration-300`}>
        <AuthLanguageSelector />
        <div className="p-6 sm:p-8">
          <div className="text-center mb-8">
            <div className="mb-4">
              <img
                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                alt="QuadCores Logo"
                className="h-16 w-auto mx-auto" />
              
            </div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {step === 'otp' ? t('security_verification') : 'Quadcores Properties'}
            </h1>
            <p className={`mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
              {step === 'otp' ?
              otpMessage || t('verification_code_sent') :
              t('login_to_continue')}
            </p>
          </div>

          {loginError &&
          <div className={`mb-6 p-3 rounded-lg flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-100 text-red-700'}`
          }>
              <RiErrorWarningLine className="me-2 flex-shrink-0" />
              <span>{loginError}</span>
            </div>
          }

          {step === 'login' ?
          <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('username')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                    <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                  type="text"
                  name="username"
                  dir="auto"
                  value={credentials.username}
                  onChange={handleChange}
                  className={`block w-full ps-10 pe-3 py-3 ${isDarkMode ?
                  'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                  'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                  errors.username ? 'border-red-500' : ''}`
                  }
                  placeholder={t('enter_username')} />
                
                </div>
                {errors.username &&
              <p className="mt-1 text-sm text-red-500">
                    {errors.username}
                  </p>
              }
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                    {t('password')}
                  </label>
                  <Link
                    to="/forgot-password"
                    className={`text-sm font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}>
                    {t('forgot_password') || 'Forgot password?'}
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                    <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  dir="ltr"
                  value={credentials.password}
                  onChange={handleChange}
                  className={`block w-full ps-10 pe-10 py-3 ${isDarkMode ?
                  'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                  'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                  errors.password ? 'border-red-500' : ''}`
                  }
                  placeholder={t('enter_password')} />

                  <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className={`absolute inset-y-0 end-0 pe-3 flex items-center ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`}>
                  
                    {showPassword ? <RiEyeOffLine size={18} /> : <RiEyeLine size={18} />}
                  </button>
                </div>
                {errors.password &&
              <p className="mt-1 text-sm text-red-500">
                    {errors.password}
                  </p>
              }
              </div>

              <div>
                <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 ${isDarkMode ?
                'bg-red-600 hover:bg-red-700' :
                'bg-red-600 hover:bg-red-700'} text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'} flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`
                }>
                
                  {isLoading ?
                <>
                      <svg className="animate-spin -ms-1 me-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('logging_in')}
                    </> :

                <>
                      <RiLoginBoxLine className="me-2" />
                      {t('login')}
                    </>
                }
                </button>
              </div>
            </form> :

          <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t("verification_code")}
                </label>
                <div className="relative">
                  <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className={`block w-full px-3 py-3 text-center text-2xl tracking-widest ${isDarkMode ?
                  'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                  'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500`
                  }
                  placeholder="123456"
                  maxLength={6} />
                
                </div>
                <p className={`mt-2 text-sm text-center ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                  {otpMessage || t('check_device_for_code')}
                </p>
              </div>

              <div>
                <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 ${isDarkMode ?
                'bg-red-600 hover:bg-red-700' :
                'bg-red-600 hover:bg-red-700'} text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'} flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`
                }>
                
                  {isLoading ?
                <>
                      <svg className="animate-spin -ms-1 me-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('verifying')}
                    </> :

                <>
                      <RiShieldKeyholeLine className="me-2" />
                      {t("verify_code")}
                    </>
                }
                </button>
              </div>

              <div className="text-center space-y-2">
                <button
                type="button"
                onClick={handleResendOtp}
                disabled={isLoading}
                className={`text-sm font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}>
                  {t("resend_code")}
                
              </button>
                <div>
                  <button
                  type="button"
                  onClick={() => setStep('login')}
                  className={`text-sm ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-700 hover:text-zinc-950'}`}>
                    {t("back_to_login")}
                  
                </button>
                </div>
              </div>
            </form>
          }

          {step === 'login' &&
          <div className="text-center mt-6">
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                {t('dont_have_account')} {' '}
                <Link to="/register" className={`font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}>
                  {t('register')}
                </Link>
              </p>
            </div>
          }
        </div>

      </div>
    </div>);

};

export default Login;